/**
 * Account-menu data model — issue #1011.
 *
 * A pure function that maps {tier, role, counts, alert state} to the structure
 * the account dropdown (desktop) and slide-out panel (mobile) render. Keeping
 * this logic out of the React components makes the tier rules unit-testable
 * without a DOM render, and guarantees desktop + mobile stay in lockstep.
 *
 * Out of scope for this issue (separate work): the /my-family hub page itself,
 * the notifications backend (dots use stub/passed-in state), and the upgrade
 * checkout flow (the CTA reuses the existing PaywallModal).
 */

import { FREE_SAVED_LIMIT } from "@/lib/constants";

export type AccountTier = "anonymous" | "free" | "premium";
export type AccountRole = "anonymous" | "parent" | "admin";

/** A leaf entry inside a dropdown section (deep-links into the hub page). */
export interface AccountMenuItem {
  id: string;
  label: string;
  href: string;
  /** Optional inline badge text, e.g. the free-tier "2 of 5 used" Saved hint. */
  badge?: string;
  /** Whether this sub-item shows an actionable notification dot. */
  showDot: boolean;
}

/** A labelled group of items. The label itself may be a link (e.g. My Family). */
export interface AccountMenuSection {
  id: string;
  label: string;
  /** When set, the section label is itself a link (e.g. /my-family). */
  href?: string;
  items: AccountMenuItem[];
}

/** A simple action button (auth entries, sign out). */
export interface AccountMenuAction {
  id: string;
  label: string;
  href: string;
}

export interface AccountMenuHeader {
  name: string;
  /** "free" | "premium" — drives the tier badge. null for anonymous. */
  tier: Exclude<AccountTier, "anonymous"> | null;
  /** Avatar initial. */
  initial: string;
  /** Avatar notification dot = OR of every sub-item dot. */
  showDot: boolean;
  // NOTE: intentionally NO `email` field — AC #1011 forbids email in the header.
}

export interface AccountMenuModel {
  header: AccountMenuHeader;
  /** Tier-aware grouped sections (My Family, Provider, Admin). */
  sections: AccountMenuSection[];
  /** Free-tier upgrade CTA, or null when not applicable. */
  upgradeCta: { label: string } | null;
  /** Auth actions for anonymous users (Sign in / Create account). Empty when signed in. */
  authActions: AccountMenuAction[];
  /** Sign-out action for signed-in users, or null when anonymous. */
  signOut: AccountMenuAction | null;
}

export interface AccountMenuInput {
  tier: AccountTier;
  role?: AccountRole;
  displayName?: string | null;
  /** Number of published camps the user has saved (drives the free "X of N" hint). */
  savedCount?: number;
  /** Free-tier save cap (defaults to the global constant). */
  savedLimit?: number;
  /** Whether the user is a claimed provider (adds the Provider section). */
  isProvider?: boolean;
}

/** Deep-link href into the /my-family hub page for a given section anchor. */
function familyAnchor(section: string): string {
  return `/my-family#${section}`;
}

/**
 * Build the account-menu model for the current viewer.
 */
export function accountMenuModel(input: AccountMenuInput): AccountMenuModel {
  const {
    tier,
    role = tier === "anonymous" ? "anonymous" : "parent",
    displayName,
    savedCount = 0,
    savedLimit = FREE_SAVED_LIMIT,
    isProvider = false,
  } = input;

  // ── Anonymous: auth actions only, no sections, no email. ──
  if (tier === "anonymous") {
    return {
      header: { name: "Account", tier: null, initial: "U", showDot: false },
      sections: [],
      upgradeCta: null,
      authActions: [
        { id: "sign-in", label: "Sign in", href: "/sign-in" },
        {
          id: "sign-up",
          label: "Create account",
          href: "/sign-in?mode=signup&next=/welcome",
        },
      ],
      signOut: null,
    };
  }

  const isPremium = tier === "premium";
  const name = displayName ?? "Account";
  const initial = (displayName ?? "U").charAt(0).toUpperCase();

  // ── My Family sub-items (premium gets Planner + Kids first). ──
  const familyItems: AccountMenuItem[] = [];

  if (isPremium) {
    familyItems.push(
      { id: "planner", label: "Planner", href: familyAnchor("planner"), showDot: false },
      { id: "kids", label: "Kids", href: familyAnchor("kids"), showDot: false },
    );
  }

  familyItems.push({
    id: "saved",
    label: "Saved",
    href: familyAnchor("saved"),
    // Free users see a usage hint; premium has unlimited saves so no badge.
    badge: isPremium ? undefined : `${savedCount} of ${savedLimit} used`,
    showDot: false,
  });

  // Registration Alerts intentionally omitted from the dropdown (#1114 Scope C):
  // the planner notification bell is the canonical real-time alerts surface, and
  // /alerts remains reachable from the My Family hub (#alerts) + the account hub.
  familyItems.push({
    id: "reviews",
    label: "Reviews",
    href: familyAnchor("reviews"),
    showDot: false,
  });

  const sections: AccountMenuSection[] = [
    { id: "my-family", label: "My Family", href: "/my-family", items: familyItems },
  ];

  // ── Provider section (claimed-listing owners only). ──
  if (isProvider) {
    sections.push({
      id: "provider",
      label: "Provider",
      items: [
        { id: "provider-dashboard", label: "Provider Dashboard", href: "/providers/dashboard", showDot: false },
        { id: "provider-listings", label: "My Listings", href: "/providers/dashboard/listings", showDot: false },
        { id: "provider-reviews", label: "Provider Reviews", href: "/providers/dashboard/reviews", showDot: false },
      ],
    });
  }

  // ── Admin section (admin role only). ──
  if (role === "admin") {
    sections.push({
      id: "admin",
      label: "Admin",
      items: [
        { id: "admin-dashboard", label: "Admin Dashboard", href: "/admin", showDot: false },
        { id: "admin-submissions", label: "Submissions", href: "/admin/submissions", showDot: false },
        { id: "admin-camps", label: "Manage Camps", href: "/admin/camps", showDot: false },
        { id: "admin-reviews", label: "Review Queue", href: "/admin/reviews", showDot: false },
      ],
    });
  }

  // Avatar dot = OR of every sub-item dot across all sections.
  const showDot = sections.some((s) => s.items.some((i) => i.showDot));

  return {
    header: { name, tier, initial, showDot },
    sections,
    upgradeCta: isPremium ? null : { label: "Upgrade to Premium" },
    authActions: [],
    signOut: { id: "sign-out", label: "Sign out", href: "/sign-out" },
  };
}
